import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

interface EditableTextProps {
  value: string;
  onSave: (newValue: string) => void;
  onHasChanges?: (hasChanges: boolean) => void;
  canCopy?: boolean;
  canEdit?: boolean;
  showCharacterCount?: boolean;
  maxLength?: number;
  autoAdjustHeightToContent?: boolean;
  additionalButtons?: React.ReactNode;
}

export const EditableText = ({
  value,
  onSave,
  onHasChanges,
  maxLength = 2000,
  canCopy = true,
  canEdit = true,
  showCharacterCount = true,
  autoAdjustHeightToContent = true,
  additionalButtons,
}: EditableTextProps) => {
  const [originalValue, setOriginalValue] = useState(value);
  const [text, setText] = useState(value);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [hasChanged, setHasChanged] = useState(false);

  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight + 2}px`;
    }
  };

  useEffect(() => {
    if (autoAdjustHeightToContent) {
      adjustTextareaHeight();
    }
    setHasChanged(text !== originalValue);
  }, [text, originalValue, autoAdjustHeightToContent]);

  useEffect(() => {
    onHasChanges?.(hasChanged);
  }, [hasChanged, onHasChanges]);

  return (
    <>
      <textarea
        ref={textareaRef}
        className="w-full textarea textarea-bordered"
        value={text}
        disabled={!canEdit}
        onChange={(e) => setText(e.target.value)}
      />

      <div className="flex justify-between items-center pt-2">
        {showCharacterCount ? (
          <div
            className={twMerge(
              'text-sm',
              text.length < maxLength ? 'text-gray-500' : 'text-red-500'
            )}
          >
            {text.length} {maxLength ? ` / ${maxLength}` : null}
          </div>
        ) : null}
        <div className="flex gap-2">
          {canCopy ? (
            <button
              onClick={() => {
                navigator.clipboard.writeText(text);
                toast.success('Teksten er kopieret til udklipsholderen');
              }}
              className="btn btn-ghost"
            >
              Kopier tekst
            </button>
          ) : null}

          {hasChanged ? (
            <button
              className="btn btn-primary"
              disabled={text.length > maxLength}
              onClick={() => {
                setHasChanged(false);
                setOriginalValue(text);
                onSave(text);
              }}
            >
              Gem ændringer
            </button>
          ) : null}

          {additionalButtons}
        </div>
      </div>
    </>
  );
};
